/**
 * Lumenate GA4 sitewide event tracker (LUM-59).
 *
 * One delegated listener set covering the whole event spec. Hooks are resolved
 * per event with closest() from `document`, never bound to elements: Horizon
 * morphs the cart on every mutation, hydrates mega-menu panels after first
 * paint and replaces carousel controls on section re-render, so any cached
 * element reference dies. Capture phase because `toggle` (<details>) does not
 * bubble — the same mechanism Horizon's own on:<event> bindings use
 * (assets/component.js registers one capturing document listener per type).
 *
 * Transport, two exits, neither of which decides anything: the event is pushed
 * to window.dataLayer for a GTM custom-event trigger, and published to
 * Shopify.analytics under a `lumenate:` prefix so a Custom Pixel or app pixel
 * can forward it (Shopify requires custom events to be namespaced). Whichever
 * one the store ends up wiring, the destination, the measurement id and the
 * consent policy live there — not here.
 *
 * Event names are snake_case renderings of the spec's "Event Name" column;
 * `event_detail` / `event_detail2` are its Event_detail columns verbatim.
 */
import { ThemeEvents } from '@theme/events';

(() => {
  // The theme editor synthesises clicks when selecting blocks, and re-renders
  // sections on every setting change. None of it is customer behaviour.
  if (window.Shopify?.designMode) return;

  const SCROLL_DEPTHS = [25, 50, 75, 90, 100];

  // Shopify only consent-gates its own sandboxed Web Pixels; theme JavaScript is
  // not covered, and a dataLayer push is replayed to GTM when the container
  // initialises, so pre-consent events would be delivered late rather than
  // dropped. Ask the Customer Privacy API directly. GTM Consent Mode is still
  // the authoritative gate for the dataLayer route — this only stops the theme
  // from collecting against an explicit refusal.
  window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], () => {});
  const consentDenied = () => window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === false;

  const usable = (value) =>
    value !== undefined &&
    value !== null &&
    value !== '' &&
    !(typeof value === 'number' && !Number.isFinite(value));

  function emit(name, detail, detail2) {
    if (consentDenied()) return;

    const params = {};
    if (usable(detail)) params.event_detail = detail;
    if (usable(detail2)) params.event_detail2 = detail2;

    // Not `??=`: an app that has set dataLayer to a non-array would otherwise
    // make every emit throw.
    if (!Array.isArray(window.dataLayer)) window.dataLayer = [];
    window.dataLayer.push({ event: name, ...params });

    // Namespaced per Shopify's custom-event rules: an unprefixed name can
    // collide with a standard event or another app's.
    window.Shopify?.analytics?.publish?.(`lumenate:${name}`, params);
  }

  /**
   * Visible label of a control. Horizon wraps labels in spans
   * (.menu-list__link-title, .wrap-text, .button-text) but also injects inline
   * SVG icon wrappers into the same element, so icon markup is skipped rather
   * than reading textContent wholesale.
   */
  function labelOf(element) {
    if (!element) return '';
    let out = '';
    for (const node of element.childNodes) {
      if (node.nodeType === Node.TEXT_NODE) out += node.textContent;
      else if (node instanceof Element && !node.matches('svg, .svg-wrapper, .details__icon')) out += node.textContent;
    }
    return out.replace(/\s+/g, ' ').trim();
  }

  function appStore(href) {
    let host;
    try {
      host = new URL(href, location.origin).hostname;
    } catch {
      return null;
    }
    if (host === 'apps.apple.com' || host === 'itunes.apple.com') return 'iOS';
    if (host === 'play.google.com') return 'Android';
    return null;
  }

  /* -------------------------------------------------------- cart quantity */

  // Horizon's capture-phase click handler is registered when component.js loads,
  // earlier in <head> than this module, so it runs first: by the time a stepper
  // click reaches onClick the component has already dispatched
  // `quantity-selector:update` carrying the authoritative new value. Buffering
  // that is exact; re-reading the input only works because the same handler
  // wrote it, so it stays as the fallback for typed changes.
  let lastQuantityUpdate = null;

  function quantityAfterChange(stepper) {
    const buffered = lastQuantityUpdate?.stepper === stepper ? lastQuantityUpdate.quantity : undefined;
    lastQuantityUpdate = null;
    return buffered ?? Number(stepper.querySelector('input[name="updates[]"]')?.value);
  }

  /* ------------------------------------------------------------------ clicks */

  function onClick(event) {
    const origin = event.composedPath?.()[0] ?? event.target;
    if (!(origin instanceof Element)) return;
    const at = (selector) => origin.closest(selector);
    let el;

    // Header — the top-level nav items are real links; the dropdown opens on
    // hover/focus, so a click there is always a navigation.
    if (at('a.header-logo')) return emit('navigation_logo_click');
    if ((el = at('a.menu-list__link'))) return emit('navigation_nav_selection', labelOf(el));
    if ((el = at('.menu-list__submenu .mega-menu__link'))) return emit('navigation_nav_sub_selection', labelOf(el));
    // Mobile drawer is a separate render from the desktop mega menu, so the two
    // trees can both be in the DOM without double-counting.
    if ((el = at('a.menu-drawer__menu-item--child, a.menu-drawer__menu-item--parent'))) {
      return emit('navigation_nav_sub_selection', labelOf(el));
    }
    if ((el = at('a.menu-drawer__menu-item--mainlist'))) return emit('navigation_nav_selection', labelOf(el));
    if (at('[aria-controls="cart-drawer"], a.action__cart')) return emit('navigation_cart_icon');
    if (at('.menu-drawer__close-button')) return setNavOpen(false);

    // Cart drawer.
    if ((el = at('button.cart-items__remove'))) {
      return emit('cart_remove_item', labelOf(el.closest('tr.cart-items__table-row')?.querySelector('.cart-items__title')));
    }
    if ((el = at('cart-quantity-selector-component button[name="plus"], cart-quantity-selector-component button[name="minus"]'))) {
      // Scoped to cart-items-component so the quick-order-list stepper is not
      // counted as a cart interaction.
      if (!at('cart-items-component')) return;
      const stepper = el.closest('cart-quantity-selector-component');
      const title = labelOf(el.closest('tr.cart-items__table-row')?.querySelector('.cart-items__title'));
      return emit(
        el.name === 'plus' ? 'cart_quantity_increase' : 'cart_quantity_decrease',
        title,
        quantityAfterChange(stepper)
      );
    }
    if ((el = at('quantity-selector-component button[name="plus"], quantity-selector-component button[name="minus"]'))) {
      return emit('pdp_change_quantity', el.name === 'plus' ? 'Increase' : 'Decrease');
    }
    if ((el = at('summary.cart-discount__summary'))) {
      // The native toggle is the click's default action, so the pre-click state
      // is still readable here. accordion-custom also rewrites `open` on
      // connect and on every breakpoint change, which the toggle event cannot
      // be told apart from a real interaction.
      if (!el.closest('details')?.open) emit('cart_open_discount_drawer');
      return;
    }
    if (at('a.cart-items__empty-button')) return emit('cart_continue_shopping');
    if (at('[data-ga4-cart-login] a[href]')) return emit('cart_login');

    // Review/quote carousel. lumenate-carousel-autoplay.js strips Horizon's
    // on:click from these buttons, so slideshow:select never fires for it —
    // the raw click is the only signal.
    if ((el = at('slideshow-component.carousel-content .slideshow-control'))) {
      const advance = el.classList.contains('slideshow-control--next');
      return emit('testimonials_carousel', advance ? 'Advance' : 'Reverse');
    }

    // Covers both accordion rows and the product disclosures block, which share
    // this summary class. The label distinguishes them.
    if ((el = at('summary.details__header'))) {
      if (!el.closest('details')?.open) emit('accordion_open', labelOf(el));
      return;
    }

    // CTAs. data-ga4-cta marks the merchant-authored button block, which is the
    // only thing `.button` reliably means — the class is shared with ~40
    // internal controls (remove, close, zoom, filters, steppers).
    if ((el = at('a[href]'))) {
      const store = appStore(el.getAttribute('href'));
      if (store) return emit('download_click', store);
    }
    if ((el = at('.hero__content-wrapper a[data-ga4-cta]'))) return emit('hero_cta_click');
    if ((el = at('.social-icons__wrapper a[href]'))) {
      return emit('footer_social_icon', (el.getAttribute('aria-label') || labelOf(el)).toLowerCase());
    }
    if ((el = at('footer .menu__item > a[href]'))) return emit('footer_selection', labelOf(el));
    if ((el = at('a[data-ga4-cta][href]'))) return emit('button_click', labelOf(el));
  }

  /* ------------------------------------------------------- mobile menu state */

  let navOpen = false;

  function setNavOpen(open) {
    if (open === navOpen) return;
    navOpen = open;
    emit(open ? 'navigation_open_nav' : 'navigation_close_nav');
  }

  /* ------------------------------------------------------------------- video */

  // Keyed on the <deferred-media> container, not the iframe: an embed set to
  // autoplay *and* given a cover image renders a poster button and an inline
  // player, so both report paths are live for the same video.
  const startedMedia = new WeakSet();
  let youtubePlayers = 0;

  function reportVideoStart(container, title) {
    if (!container || startedMedia.has(container)) return;
    startedMedia.add(container);
    emit('video_start', title);
  }

  function loadYouTubeApi() {
    if (window.YT?.Player) return Promise.resolve();
    // youtube-nocookie.com serves no /iframe_api (404), so the API can only come
    // from youtube.com even though the embeds themselves are pinned to the
    // privacy host. Content blockers routinely drop it — without the onerror
    // rejection a blocked script leaves every caller awaiting forever.
    window.lumenateYouTubeApiReady ??= new Promise((resolve, reject) => {
      const previous = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        resolve();
        // A foreign callback must not get to decide whether our tracking runs.
        try {
          previous?.();
        } catch {
          /* not ours to handle */
        }
      };
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.onerror = reject;
      document.head.append(script);
    });
    return window.lumenateYouTubeApiReady;
  }

  async function bindYouTubePlayer(frame) {
    await loadYouTubeApi();
    frame.id ||= `lumenate-youtube-${(youtubePlayers += 1)}`;
    const container = frame.closest('deferred-media') ?? frame;
    const report = ({ data }) => {
      if (data === window.YT.PlayerState.PLAYING) {
        reportVideoStart(container, container.dataset?.ga4VideoTitle || frame.title);
      }
    };
    new window.YT.Player(frame.id, {
      events: {
        // The API is fetched on first visibility, so playback can already be
        // running by the time the player attaches.
        onReady: ({ target }) => report({ data: target.getPlayerState() }),
        onStateChange: report,
      },
    });
  }

  /**
   * Instruments YouTube embeds that render inline. Poster-gated embeds live in a
   * <template> until clicked and report through `media:started-playing`.
   * Deferring each API load to first visibility keeps the request to
   * youtube.com — the cookie-bearing host these embeds deliberately avoid — off
   * pages where the visitor never reaches the video.
   */
  function trackYouTubeEmbeds() {
    const frames = document.querySelectorAll('iframe.js-youtube');
    if (!frames.length) return;

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.unobserve(entry.target);
        bindYouTubePlayer(entry.target).catch(() => {
          /* API blocked, or the section was re-rendered away. Nothing to report. */
        });
      }
    });
    frames.forEach((frame) => observer.observe(frame));
  }

  /* ------------------------------------------------------------ scroll depth */

  function trackScrollDepth() {
    const pending = new Set(SCROLL_DEPTHS);
    // Desktop scrolls .page-wrapper, not the document — Horizon locks html/body
    // to overflow:hidden above 990px.
    const wrapper = document.querySelector('.page-wrapper');
    let ticking = false;

    // Share of the page seen, matching how GTM's own scroll trigger measures it:
    // the bottom edge of the viewport, not the scroll offset. A page barely
    // taller than the viewport therefore clears several thresholds at once.
    const reached = (element) => {
      if (!element || element.scrollHeight <= element.clientHeight) return 0;
      return Math.min(100, ((element.scrollTop + element.clientHeight) / element.scrollHeight) * 100);
    };

    const measure = () => {
      ticking = false;
      const depth = Math.max(reached(wrapper), reached(document.scrollingElement));
      for (const threshold of SCROLL_DEPTHS) {
        // Half a percent of slack: fractional scrollTop and zoomed viewports
        // stop the bottom threshold from ever being reached exactly.
        if (depth + 0.5 < threshold || !pending.delete(threshold)) continue;
        emit('scroll_depth', threshold);
      }
      if (!pending.size) stop();
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(measure);
    };

    function stop() {
      wrapper?.removeEventListener('scroll', onScroll);
      window.removeEventListener('scroll', onScroll);
    }

    wrapper?.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* -------------------------------------------------------------------- wire */

  document.addEventListener('click', onClick, true);

  document.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
    lastQuantityUpdate = { stepper: event.target, quantity: event.detail?.quantity };
  });

  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (details instanceof HTMLDetailsElement && details.id === 'Details-menu-drawer-container') {
        setNavOpen(details.open);
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('form.cart-discount__form')) return;
      // Read before cart-discount.js clears the field. Only a code-shaped token
      // is reported: this fires before the theme validates anything, so a pasted
      // email or sentence would otherwise land in GA4 as an event parameter.
      const code = form.querySelector('input[name="discount"]')?.value.trim();
      emit('cart_apply_discount', /^[\w-]{1,64}$/.test(code ?? '') ? code : undefined);
    },
    true
  );

  document.addEventListener('theme-drawer:close', (event) => {
    if (event.target instanceof Element && event.target.id === 'cart-drawer') emit('cart_close_cart');
  });

  document.addEventListener(ThemeEvents.mediaStartedPlaying, (event) => {
    const container = event.detail?.resource ?? event.target;
    if (!(container instanceof Element)) return;
    // ProductModel extends DeferredMedia, so a 3D-model poster dispatches this
    // too. media.js clones the <template> *after* dispatching, so the media type
    // and title have to be read out of the template rather than the document.
    const media = container.querySelector('template')?.content.querySelector('video, iframe');
    if (!media) return;
    reportVideoStart(container, container.dataset?.ga4VideoTitle || media.title);
  });

  function setup() {
    trackScrollDepth();
    trackYouTubeEmbeds();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();
